import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { withFormik } from 'formik';
import * as yup from 'yup';
import moment from 'moment';
import { toast } from "react-toastify";

import Loading from "../../../../../../components/loading";
import BookingDetails from './components/BookingDetails';
import GuestDetails from './components/GuestDetails/GuestDetails';
import Payment from './components/Payment';
import Invoice from './components/Invoice';
import { editGuest } from "../../../../../../general_redux/guest/actions";
import { clearInvoice } from '../../../../../../general_redux/invoice/actions';
import { getDateObjFromString } from '../../../../../../common/functions/utils';
import { getGuestByBooking, clearGuest } from '../../../../../../general_redux/guest/actions';
import {
  editBooking,
  fetchBookingById,
  getChannelBookingDetailsByBookingId,
  resetCharges,
  fetchDiscountByBooking,
  editDiscountByBookingForm,
  clearBooking,
  clearFessTaxes,
} from '../../../../../../general_redux/calendar/actions';

import styles from './editBooking.module.css';


const EditBooking = ({ bookingId, setViewEditBooking }) => {
  
  const dispatch = useDispatch();
  
  const user = useSelector(({ generalSetting }) => generalSetting && generalSetting.setting);
  const rentalList = useSelector(({ rentals }) => rentals && rentals.rentals);
  const bookingData = useSelector(({ calendar }) => calendar && calendar.bookingById);
  const bookingDiscount = useSelector(({ calendar }) => calendar && calendar.bookingDiscount);
  const generalSetting = useSelector(({ generalSetting }) => generalSetting && generalSetting.setting);
  const guestDetails = useSelector(({ guests }) => guests && guests.guestList.find(guest => guest.id === bookingData.guestId));
  const bookerDetails = useSelector(({ guests }) => guests && guests.guestList.find(guest => guest.id === bookingData.bookerId));
  const actionForFetchDiscount = useSelector(({ calendar }) => calendar && calendar.actionForFetchDiscount);
  const actionForFetchBooking = useSelector(({ calendar }) => calendar && calendar.actionForFetchBooking);

  const [currentTab, setCurrentTab] = useState('Booking Details');
  const [paymentStatus] = useState(bookingData?.paymentStatus || 'Payment Pending');
  
  useEffect(() => {
    dispatch(getGuestByBooking(bookingId));
    dispatch(fetchDiscountByBooking(bookingId));
    dispatch(fetchBookingById(bookingId));
    dispatch(getChannelBookingDetailsByBookingId(bookingId));
  }, []);

  useEffect(()=>{
    return()=>{
      dispatch(clearGuest());
      dispatch(resetCharges());
      dispatch(clearInvoice());
      dispatch(clearBooking());
      dispatch(clearFessTaxes());
    }
  },[])

  const tabs = ['Booking Details', 'Guest Details', 'Payment', 'Invoices'];
  const guestName = guestDetails ? guestDetails.name : bookingData?.guestName;

  const ota = (() => {
    // TODO: create mapper according to the OTA dictionary
    const source = bookingData?.source;
    const OTAs = ['AIRBNB','BEEHAZ','AGODA','BOKKINGCOM','VRBO','OTHERS'];
    const ota = source && OTAs.find(item => item === source.toUpperCase());

    if (!ota) {
        return '';
    };
    if (ota === 'BOKKINGCOM') { 
        return 'Booking.com';
    };
    if (ota === 'VRBO') { 
        return 'VRBO';
    };
    return ota.charAt(0).toUpperCase() + ota.slice(1).toLowerCase();

  })();

  const findRentalDetail = (rentalId) => {
    const currency = generalSetting.currency.value;
    const rentalDetail = rentalList.find((rental) => Number(rental.id) === Number(rentalId));
    return rentalDetail || {
      id: '',
      arrive: '',
      maxOccup: 1,
      maxChild: 0,
      checkInTimeAt: 'from',
      checkOutTimeAt: 'until',
      checkinTime: '15:00',
      checkoutTime: '10:00',
      currency: currency,
    };
  };

  const rentalDetails = findRentalDetail(bookingData?.rentalId);

  const getChannelName = (channel) => {
    if (channel.ota === 'Other')
        return {label: `${channel.name} (iCal)`, value: channel.name}
    if (channel.ota === 'VRBO')
        return {label: 'VRBO (HomeAway) (iCal)', value: channel.ota}
    else
        return {label: `${channel.ota} (iCal)`, value: channel.ota}
  };

  const getChannelOptions = (channels) => {
    const channelOptions = channels.map((item) => {
      const ChannelNameObject = getChannelName(item.channel);
      return {label: ChannelNameObject.label, value: ChannelNameObject.value};
    });
    return [{label: 'Beehaz', value: 'Beehaz'}, ...channelOptions];
  };

  const certainTypesOfReservationsWarning = () => {

    if (bookingData.bookingType === 'channelBooking') {
      toast.warn(
        <div style={{ color: "#000" }}>
          <p style={{ fontWeight: "700" }}>This action is not recommended!</p>
          <p>This is a channel reservation — date changes will not be reflected on the original OTA booking.</p>
        </div>
      )
    };

    if (bookingData.bookingType === "monthly" || bookingData.bookingType === "weekly") {
      toast.warn(
          <div style={{ color: "#000" }}>
            <span>
              You are modifying a fixed-term reservation.
            </span>
            <br />
            <span>
              Note the prices will be recalculated using this special rates and not your usual rates.
            </span>
          </div>
        )
    };

  };

    const getContactInitialValues = (data) => {
        return {
            id: data?.id || null,
            name: data?.name || "",
            emailId: data?.emailId || "",
            secondaryEmailId: data?.secondaryEmailId || "",
            phoneNo: data?.phoneNo || "",
            secondaryPhoneNo: data?.secondaryPhoneNo || "",
            postalCode: data?.postalCode || "",
            country: data?.country || user.country,
            address: data?.address || "",
            street: data?.street || "",
            state: data?.state || "",
            nationality: data?.nationality || "",
            language: data?.language || "",
            company: data?.company || "",
            companyAddress: data?.companyAddress || "",
            notes: data?.notes || "",
            dob: data?.dob && !["Invalid da", "", null, "[NULL]"].includes(data.dob) ? new Date(moment(data.dob)) : "",
        };
    };

  const handleSubmit = (data) => {

    const { bookingPayload, guestPayload, bookerPayload, discountPayload } = data.payloads;

    dispatch(editBooking(bookingPayload)).then(() => {
      
      dispatch(editDiscountByBookingForm(discountPayload, bookingDiscount.id));
      
      // notes field in the form (Guest - More info:)
      if (guestPayload.notes !== guestDetails?.notes) {
            let _guestPayload = getContactInitialValues(guestDetails);
            _guestPayload["notes"] = guestPayload.notes;
            dispatch(editGuest(_guestPayload));
      };
      
      // notes field in the form (Booker - More info:) - only if the guest and the booker are not the same person
      if (bookerPayload.notes !== bookerDetails?.notes && bookerPayload.id !== guestPayload.id) {
            let _bookerPayload = getContactInitialValues(bookerDetails);
            _bookerPayload["notes"] = bookerPayload.notes;
            dispatch(editGuest(_bookerPayload));
      };
    
    })

    setViewEditBooking(false);

  };

  const otherInitialValues = {
    reservationLink: "", // TODO: 
    checkboxAdditionalGuests: bookingData?.additionalGuestNames || '',
    maxOccup: rentalDetails.maxOccup,
    maxChild: rentalDetails.maxChild,
    guestName: guestName,
    guest: {
      notes: guestDetails?.notes || '',
    },
    booker: {
      notes: bookerDetails?.notes || '',
    },
  };

  const initialValues = {
    
    // Booking - initialValues
    id: bookingData?.id || 0,
    bookingType: bookingData?.bookingType || 'booking',
    rentalId: bookingData?.rentalId || '',
    guestId: bookingData?.guestId || 0,
    bookerId: bookingData?.bookerId || 0,
    suBookingId: bookingData?.suBookingId || 0,
    channelBookingId: bookingData?.channelBookingId || "N/A",
    noOfGuests: bookingData?.noOfGuests || 1,
    noOfAdults: bookingData?.noOfAdults || 1,
    noOfChildren: bookingData?.noOfChildren || 0,
    noOfBabies: bookingData?.noOfBabies || 0,
    noOfPets: bookingData?.noOfPets || 0,
    netAmount: bookingData?.netAmount || 0,
    additionalGuestNames: bookingData?.additionalGuestNames || '',
    arrive: bookingData?.arrive ? getDateObjFromString(bookingData.arrive) : new Date(),
    depart: bookingData?.depart ? getDateObjFromString(bookingData.depart) : new Date(),
    checkInTimeAt: bookingData?.checkInTimeAt || 'from',
    checkOutTimeAt: bookingData?.checkOutTimeAt || 'until',
    checkInTime: bookingData?.checkInTime || user.checkInTime,
    checkOutTime: bookingData?.checkOutTime || user.checkOutTime,
    nights: bookingData?.nights || 0,
    status: bookingData?.status ||"Booked",
    source: bookingData?.source || 'Beehaz',
    discount: 0,
    extraCharge: 0,
    price: bookingData?.price || 0.0,
    paymentStatus: bookingData?.paymentStatus || 'Payment Pending',
    miniNotes: bookingData?.miniNotes || '',
    notes: `${bookingData?.notes ? bookingData.notes : ''}`,
    color: bookingData?.color || '',

    // Other initial values
    ...otherInitialValues,

  };

  const validationSchema = yup.object().shape({
    rentalId: yup.number().required('Rental selection is required').integer('Must be an integer').typeError('Rental selection is required'),
    noOfGuests: yup.number().required('At least 1 guest is required').min(1, 'Must be a number greater than or equal to 1').integer('Must be an integer').typeError('Must be a value of type number')
      .test('noOfGuests', function (value) {
        const maxOccup = this.parent.maxOccup;
        return value <= maxOccup ? true : this.createError({
          path: this.path,
          message: `Maximum ${maxOccup} guests are allowed`,
        });
    }),
    noOfAdults: yup.number().required('At least 1 adult is required').min(1, 'At least 1 adult is required').integer('Must be an integer').typeError('Must be a value of type number'),
    noOfChildren: yup.number().min(0, 'Must be a positive number').integer('Must be an integer').typeError('Must be a value of type number')
      .test('noOfChildren', function (value) {
        const maxChild = this.parent.maxChild;
        return value <= maxChild || !value ? true : this.createError({
          path: this.path,
          message: `Maximum ${maxChild} children are allowed`,
        });
    }),
    noOfBabies: yup.number().min(0, 'Must be a positive number').integer('Must be an integer').typeError('Must be a value of type number'),
    noOfPets: yup.number().min(0, 'Must be a positive number').integer('Must be an integer').typeError('Must be a value of type number'),
    netAmount: yup.number().min(0, 'Must be a positive number').typeError('Must be a value of type number'),
    arrive: yup.date().required('Arrival date is required').typeError('Must be a value of type date').max(`${moment().add(2,'years').format('YYYY-MM-DD')}`, 'Booking not allowed after 2 years'),
    depart: yup.date().required('Departure date is required').typeError('Must be a value of type date').max(`${moment().add(2,'years').format('YYYY-MM-DD')}`, 'Booking not allowed after 2 years'),
    checkInTime: yup.string().required('Check-in is required'),
    checkOutTime: yup.string().required('Check-out is required'),
    nights: yup.number().min(0, 'Must be a positive number').integer('Must be an integer').typeError('Must be a value of type number')
      .test('nights', function (value) {
        let message;
        const minStay = this.parent.minStay;
        const maxStay = this.parent.maxStay;
        if (value < minStay) {
          message = `Minimum stay for this rental is ${minStay} day${minStay > 1 ? 's' : ''}`;
        } else if (value > maxStay && maxStay > 0) {
          message = `Maximum stay for this rental is ${maxStay} day${maxStay > 1 ? 's' : ''}`;
        };
        return !message ? true : this.createError({
          path: this.path,
          message: message,
        });
    }),
    source: yup.string(),
    paymentStatus: yup.string(),
    price: yup.number().min(0, 'Must be a positive number').typeError('Must be a value of type number'),
    additionalGuestNames: yup.string().max(150, 'Must be 150 characters or less'),
    miniNotes: yup.string().max(150, 'Maximum 150 characters are allowed'),
    notes: yup.string().max(900, 'Maximum 900 characters are allowed'),
    color: yup.string(),

    // Guest validation schema
    guest: yup.object().shape({
      notes: yup.string().max(250, 'Must be 250 characters or less')
    }),
    
    // Booker validation schema
    booker: yup.object().shape({
      notes: yup.string().max(250, 'Must be 250 characters or less')
    }),

  });

  const FormikEnhancer = withFormik({
    displayName: 'BookingDetails',
    validationSchema: validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    mapPropsToValues: () => (initialValues),
    handleSubmit: (values, { setSubmitting }) => {
      handleSubmit(values);
      setSubmitting(false);
    },
  })(BookingDetails);


  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title className={styles.modalTitle}>Edit booking</Modal.Title>
      </Modal.Header>
      <Modal.Body className={`px-sm-2 px-0 ${styles.modal_body}`}>
        <div className={styles.upperRow}>
          {tabs.map((tab, index) => (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
              className={currentTab === tab ? `${styles.tabBox} ${styles.active}` : `${styles.tabBox}`}
              key={index}
              onClick={() => setCurrentTab(tab)}
            >
              {tab}
            </a>
          ))}
        </div>
        <div>
          {currentTab === 'Booking Details' && (
            <div className="mt-3">
              <Loading
                loadingStatus={
                  actionForFetchBooking.loading ||
                  actionForFetchDiscount.loading
                }
              />
              <FormikEnhancer
                ota={ota}
                bookingId={bookingId}
                getChannelOptions={getChannelOptions}
                certainTypesOfReservationsWarning={certainTypesOfReservationsWarning}
              />
            </div>
          )}
          {currentTab === 'Guest Details' &&
            <GuestDetails
              bookingData={bookingData}
              guestDetails={guestDetails}
              bookerDetails={bookerDetails}
            />
          }
          {currentTab === 'Payment' && <Payment bookingId={bookingId} />}
          {currentTab === 'Invoices' && <Invoice bookingId={bookingId}/>}
        </div>
      </Modal.Body>
    </>
  );
};

export default EditBooking;
